import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Text } from "../../../../components/Text";
import { TestIDs } from "../../../../tests/types/testIds";
import { ConversationMessage } from "../../../../model/integration/dto/meta/MetaConversationMessage";
import { Rounded } from "../../../../theme/Rounded";
import { Shadow } from "../../../../theme/Shadow";
import { Spacing } from "../../../../theme/Spacing";
import { WAMessageReactionsModal } from "./WAMessageReactionsModal";
import { useWAMessageReaction } from "./useWAMessageReaction";

const Row = styled.div<{ $isSender: boolean }>`
  position: absolute;
  bottom: -0.7rem;
  ${({ $isSender }) =>
    $isSender ? "inset-inline-end" : "inset-inline-start"}: ${Spacing.s};
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 1px ${Spacing.xs};
  border-radius: ${Rounded.pill};
  background-color: ${({ theme }) => theme.white};
  border: 1px solid ${({ theme }) => theme.sub};
  box-shadow: ${Shadow.SM};
  z-index: 1;
`;

const Chip = styled.button`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 0 1px;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: ${Rounded.pill};

  &:hover {
    background-color: ${({ theme }) => theme.sub};
  }
`;

type Props = {
  message: ConversationMessage;
  isSender: boolean;
};

export const WAMessageReactions: React.FC<Props> = ({ message, isSender }) => {
  const [detailsOpen, setDetailsOpen] = useState(false);

  const { t } = useTranslation();
  const { ownReaction, react } = useWAMessageReaction(message);

  const reactions = message.reactions || [];

  if (!reactions.length) {
    return null;
  }

  const counts = reactions.reduce<Record<string, number>>((acc, reaction) => {
    acc[reaction.emoji] = (acc[reaction.emoji] || 0) + 1;
    return acc;
  }, {});

  return (
    <>
      <Row $isSender={isSender}>
        {Object.entries(counts).map(([emoji, count]) => {
          const isOurs = ownReaction?.emoji === emoji;

          return (
            <Chip
              key={emoji}
              type="button"
              data-testid={TestIDs.integration.whatsappMeta.chat.reaction(
                emoji,
              )}
              title={
                isOurs
                  ? t("integration.whatsapp.meta.reactions.remove")
                  : t("integration.whatsapp.meta.reactions.title")
              }
              onClick={(event) => {
                event.stopPropagation();

                if (isOurs) {
                  react(emoji);
                  return;
                }

                setDetailsOpen(true);
              }}
            >
              <Text fontSize="xs" color="textColor">
                {emoji}
                {count > 1 ? ` ${count}` : ""}
              </Text>
            </Chip>
          );
        })}
      </Row>

      {detailsOpen ? (
        <WAMessageReactionsModal
          open
          onClose={() => setDetailsOpen(false)}
          message={message}
        />
      ) : null}
    </>
  );
};
